// src/components/reports/preview/SickLeaveReportPreview.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import SickLeaveReportModel from '../../../models/SickLeaveReportModel';

// ─────────────────────────────────────────────────────────────────────────────
// Sick Leave Overview - One row per sick leave day entry, sorted by date then staff
// ─────────────────────────────────────────────────────────────────────────────

const SickLeaveOverviewView = ({ data, isPreview, previewLimit }) => {
  const { t } = useTranslation('reports');

  // Sort by date ascending, then staff name alphabetically.
  // Mirrors the sort order applied in ReportService.loadSickLeaveEntries.
  const sorted = [...data].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.staffName.localeCompare(b.staffName);
  });

  const displayData = isPreview ? sorted.slice(0, previewLimit) : sorted;

  return (
    <div className="report-preview sick-leave-overview-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="date-col">{t('date')}</th>
              <th className="staff-col">{t('staff')}</th>
            </tr>
          </thead>

          <tbody>
            {displayData.map((entry, index) => (
              <tr
                key={`${entry.staffId}-${entry.date}-${index}`}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{entry.getFormattedDate()}</td>
                <td>{entry.staffName}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td><strong>{t('total')}</strong></td>
              <td>
                <strong>
                  {data.length} {t('sickDays')}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && data.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: data.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sick Leave Summary - One aggregated row per staff member showing total days
// ─────────────────────────────────────────────────────────────────────────────

const SickLeaveSummaryView = ({ data, isPreview, previewLimit }) => {
  const { t } = useTranslation('reports');

  // buildSummaryRows counts all entries as days and sorts by staff name.
  // Duration is not tracked in sick leave reports.
  const summaryRows = SickLeaveReportModel.buildSummaryRows(data);

  const displayRows = isPreview ? summaryRows.slice(0, previewLimit) : summaryRows;

  // Grand total computed from full summaryRows, not just displayed rows
  const grandTotalDays = summaryRows.reduce((sum, row) => sum + row.totalDays, 0);

  return (
    <div className="report-preview sick-leave-summary-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="staff-col">{t('staff')}</th>
              <th className="count-col">{t('sickDays')}</th>
            </tr>
          </thead>

          <tbody>
            {displayRows.map((row, index) => (
              <tr
                key={row.staffId}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{row.staffName}</td>
                <td>{row.totalDays}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td><strong>{t('total')}</strong></td>
              <td><strong>{grandTotalDays}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && summaryRows.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: summaryRows.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Preview component for Sick Leave report types.
 * Handles both Sick Leave Overview (one row per sick leave day entry) and
 * Sick Leave Summary (aggregated day count per staff member).
 *
 * Duration values are not displayed in sick leave reports.
 * Each Firestore sickLeave document represents exactly one sick leave day.
 *
 * Props are passed from ReportPreview.js - this component does not
 * read from context directly.
 *
 * @param {SickLeaveReportModel[]} props.data - Array of SickLeaveReportModel instances
 * @param {string} props.reportType - 'Sick Leave Overview' or 'Sick Leave Summary'
 * @param {boolean} props.isPreview - Whether to limit the displayed rows
 * @param {number} props.previewLimit - Maximum rows shown in preview mode
 */
const SickLeaveReportPreview = ({
  data,
  reportType,
  isPreview = true,
  previewLimit = 10
}) => {
  if (reportType === 'Sick Leave Summary') {
    return (
      <SickLeaveSummaryView
        data={data}
        isPreview={isPreview}
        previewLimit={previewLimit}
      />
    );
  }

  return (
    <SickLeaveOverviewView
      data={data}
      isPreview={isPreview}
      previewLimit={previewLimit}
    />
  );
};

export default SickLeaveReportPreview;